import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // ✅ Import `useParams`

import {
    TextField, Card, CardContent, Typography, Box, CircularProgress, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Select, MenuItem, Dialog,
    DialogActions,
    DialogContent
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { AddCircle as AddCircleIcon, Today } from "@mui/icons-material";
import { WhatsApp as WhatsAppIcon } from "@mui/icons-material";

const EventDetails = ({ shareIdentifier: propShareIdentifier }) => {
    const { shareIdentifier: paramShareIdentifier } = useParams();
    const [eventDetails, setEventDetails] = useState(null);
    const [rounds, setRounds] = useState([]);
    const [producers, setProducers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [supplierPayments, setSupplierPayments] = useState([]);
    const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [editedEventName, setEditedEventName] = useState("");
    const [editedDescription, setEditedDescription] = useState("");
    const [showEventNameActions, setShowEventNameActions] = useState(false);
    const [showDescriptionActions, setShowDescriptionActions] = useState(false);
    // For Refund All Participants
    const [openRefundDialog, setOpenRefundDialog] = useState(false);
    const [refundLoading, setRefundLoading] = useState(false);
    const [refundResult, setRefundResult] = useState(null);
    const [openRefundResultDialog, setOpenRefundResultDialog] = useState(false);

    const [editedRounds, setEditedRounds] = useState([]);
    const [roundsPendingEdits, setRoundsPendingEdits] = useState({}); // roundId → { name, price }

    const [editedProducers, setEditedProducers] = useState([]);
    const [producersPendingEdits, setProducersPendingEdits] = useState({}); // producerId → { firstName, lastName }
    const navigate = useNavigate();



    // ✅ Ensure correct `shareIdentifier` is used
    const finalShareIdentifier = propShareIdentifier || paramShareIdentifier;

    const [newPaymentRow, setNewPaymentRow] = useState({
        supplier_id: "",
        supplierType: "",
        supplierPhone: "",
        payment: "",
        paymentDate: new Date().toISOString().split("T")[0],
    });

    useEffect(() => {
        document.body.style.backgroundColor = "#121212";
        document.body.style.color = "#fff";
        return () => {
            document.body.style.backgroundColor = "";
            document.body.style.color = "";
        };
    }, []);

    useEffect(() => {
        if (finalShareIdentifier) {
            fetchSuppliers(); // ✅ New: Fetch supplier list
            fetchEventDetails(finalShareIdentifier);
            fetchSupplierPayments(finalShareIdentifier);
            console.log("📝 Suppliers Data:", JSON.stringify(supplierPayments, null, 2));

        }
    }, [finalShareIdentifier]); // ✅ Fetch when identifier changes

    // Complete fix for rounds editing issues

    const fetchEventDetails = async (identifier) => {
        if (!identifier) {
            setError("Please enter a valid Share Identifier.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/events/${identifier}`);
            if (!response.ok) throw new Error("Event not found.");
            const eventData = await response.json();
            setEventDetails(eventData);

            setEditedEventName(eventData.title || "");
            setEditedDescription(eventData.description || "");

            // Fetch event rounds
            const roundsResponse = await fetch(`${process.env.REACT_APP_API_URL}/events/${identifier}/rounds`);
            if (!roundsResponse.ok) throw new Error("Rounds not found.");
            const roundsData = await roundsResponse.json();

            const formattedRounds = roundsData
                .map((round, index) => ({
                    roundId: round.round_id ?? "N/A",
                    roundName: round.round_name ?? "N/A",
                    originalRoundName: round.round_name ?? "N/A", // Keep original for reset
                    ticketType: round.ticketType ?? "N/A",
                    maxInstantPurchase: round.maxInstantPurchase ?? "N/A",
                    sellStartDate: round.sell_start ? new Date(round.sell_start) : null,
                    sellStartDateString: round.sell_start ? new Date(round.sell_start).toLocaleString() : "N/A",
                    sellEndDate: round.sell_end ? new Date(round.sell_end).toLocaleString() : "N/A",
                    price_NIS: round.price_nis ?? "0.00",
                    originalPrice: round.price_nis ?? "0.00", // Keep original for reset
                    fee: round.event_fee ?? "0.00",
                    totalParticipants: round.totalparticipants ?? 0,
                    approvedParticipants: round.approvedparticipants ?? 0,
                    totalPaid: round.totalpaid ?? "0.00",
                    isPrivate: round.privateLinkId ? "Yes" : "No",
                    arrayIndex: index // Track original array position
                }))
                .sort((a, b) => (a.sellStartDate && b.sellStartDate ? a.sellStartDate - b.sellStartDate : 0));

            console.log("Setting formatted rounds:", JSON.stringify(formattedRounds, null, 2));
            setRounds(formattedRounds);

            // Clear any pending edits and edited rounds when fetching fresh data
            setEditedRounds([]);
            setRoundsPendingEdits({});

            // Rest of producers logic...
            const producersResponse = await fetch(`${process.env.REACT_APP_API_URL}/events/${identifier}/producers`);
            if (!producersResponse.ok) throw new Error("Producers not found.");
            const producersData = await producersResponse.json();
            setProducers(producersData);

            const initialEditedProducers = producersData.map(p => ({
                producerId: p.producerId,
                firstName: p.firstName || "",
                lastName: p.lastName || ""
            }));
            setEditedProducers(initialEditedProducers);
            setProducersPendingEdits({});

        } catch (err) {
            setError(err.message);
            setEventDetails(null);
            setRounds([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/suppliers`);
            if (!response.ok) throw new Error("Failed to fetch suppliers.");
            const data = await response.json();
            console.log("📝 Suppliers Data:", JSON.stringify(data, null, 2));
            setSuppliers(data);
        } catch (error) {
            console.error("❌ Error fetching suppliers:", error);
        }
    };


    const fetchSupplierPayments = async (identifier) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/supplier_payments/${identifier}`);

            if (!response.ok) {
                console.error("❌ Server responded with an error:", response.status);
                setSupplierPayments([]); // ✅ Ensure it's always an array
                return;
            }

            const text = await response.text();

            if (!text.trim()) {
                console.warn("⚠️ Empty response from server.");
                setSupplierPayments([]); // ✅ Prevents undefined state
                return;
            }

            const data = JSON.parse(text);
            console.log("suppliers payments:", JSON.stringify(data));

            const formattedData = Array.isArray(data) && data.length > 0
                ? data.map(row => ({
                    id: row.id,
                    supplierId: row.supplier_id || "",
                    supplierType: row.supplier_type || "",
                    supplierName: row.supplier_name || "",
                    supplierPhone: row.supplier_phone || "",
                    payment: row.payment || "0.00",
                    paymentDate: row.payment_date || new Date().toISOString().split("T")[0]
                }))
                : [];

            setSupplierPayments(formattedData);
        } catch (err) {
            console.error("❌ Error fetching supplier payments:", err);
            setSupplierPayments([]); // ✅ Ensures an empty array instead of undefined
        }
    };




    const handleOpenAddPaymentDialog = () => {
        setNewPaymentRow({
            supplier_id: "",
            supplierType: "",
            supplierPhone: "",
            payment: "",
            paymentDate: new Date().toISOString().split("T")[0],
        });
        setOpenPaymentDialog(true);
    };

    const handleConfirmAddPayment = async () => {
        if (!newPaymentRow.supplier_id || !newPaymentRow.payment || !newPaymentRow.paymentDate) {
            alert("Please fill in all required fields.");
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/supplier_payments/${finalShareIdentifier}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newPaymentRow),
            });

            if (!response.ok) throw new Error("Failed to add supplier payment.");

            setOpenPaymentDialog(false);
            fetchSupplierPayments(finalShareIdentifier);
        } catch (error) {
            console.error("❌ Error adding supplier payment:", error);
        }
    };

    const handleCancelAddPayment = () => {
        setOpenPaymentDialog(false);
    };


    const handleEditCell = (index, field, value) => {
        setSupplierPayments((prevPayments) => {
            const updatedPayments = [...prevPayments];
            updatedPayments[index] = { ...updatedPayments[index], [field]: value };
            return updatedPayments; // Only update state, do not send request yet
        });
    };


    const handleCellBlur = async (index, field) => {
        const updatedRow = supplierPayments[index];

        if (!updatedRow.id) return; // Skip if the row doesn't exist in the database yet

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/supplier_payments/${updatedRow.id}`, {
                method: "PATCH", // ✅ Use PATCH for partial update
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ [field]: updatedRow[field] }), // Send only changed field
            });

            if (!response.ok) throw new Error("Failed to update supplier payment.");

            console.log(`✅ Updated supplier ${field}:`, updatedRow[field]);
        } catch (error) {
            console.error("❌ Error updating supplier payment:", error);
        }
    };




    const handleDeleteRow = async (id, index) => {
        if (!id) {
            // Just remove from UI if not saved in DB
            setSupplierPayments(supplierPayments.filter((_, i) => i !== index));
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/supplier_payments/${id}`, {
                method: "DELETE",
            });

            if (!response.ok) throw new Error("Failed to delete supplier payment.");

            // Remove from state
            setSupplierPayments(supplierPayments.filter((_, i) => i !== index));
        } catch (error) {
            console.error("❌ Error deleting supplier payment:", error);
        }
    };

    // Function to handle the refund all process
    const handleRefundAllParticipants = async () => {
        setRefundLoading(true);
        setOpenRefundDialog(false);
        
        try {
            const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/admin/event/${eventDetails.id}/cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-API-Key': process.env.REACT_APP_ADMIN_API_KEY,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to refund participants: ${response.status}`);
            }

            const result = await response.json();
            setRefundResult(result);
            setOpenRefundResultDialog(true);
            
            // Optionally refresh the event details to reflect changes
            if (finalShareIdentifier) {
                fetchEventDetails(finalShareIdentifier);
            }
            
        } catch (error) {
            console.error('❌ Error refunding participants:', error);
            setRefundResult({
                message: `Error: ${error.message}`,
                details: {
                    totalParticipants: 0,
                    successfulRejections: 0,
                    failedRejections: 0
                }
            });
            setOpenRefundResultDialog(true);
        } finally {
            setRefundLoading(false);
        }
    };

    // ✅ Fixed rounds table rendering with proper state management
    const renderRoundsTable = () => (
        <Box sx={{ marginTop: 3 }}>
            <Typography variant="h6">Event Rounds</Typography>
            <Box sx={{ overflowX: "auto", bgcolor: "#1e1e1e", padding: 2, borderRadius: "5px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#333", color: "#fff" }}>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Round Name</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Ticket Type</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Max Instant Purchase</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Sell Start</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Sell End</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Price (NIS)</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Fee (NIS)</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Participants</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Approved Participants</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Total Paid (NIS)</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Private</th>
                            <th style={{ padding: "8px", borderBottom: "1px solid #555" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rounds.map((round, index) => {
                            const hasPendingEdits = roundsPendingEdits[round.roundId];
                            
                            // Get current values from pending edits or fall back to original
                            const currentRoundName = hasPendingEdits?.name !== undefined 
                                ? hasPendingEdits.name 
                                : round.roundName;
                            
                            const currentPrice = hasPendingEdits?.price !== undefined 
                                ? hasPendingEdits.price 
                                : round.price_NIS;
                            
                            return (
                                <tr key={`round-${round.roundId}`} style={{ backgroundColor: index % 2 === 0 ? "#2a2a2a" : "#1e1e1e" }}>
                                    <td style={{ padding: "8px" }}>
                                        <TextField
                                            variant="standard"
                                            value={currentRoundName}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                
                                                setRoundsPendingEdits(prev => ({
                                                    ...prev,
                                                    [round.roundId]: {
                                                        ...prev[round.roundId],
                                                        name: newValue,
                                                    },
                                                }));
                                            }}
                                            sx={{ 
                                                input: { 
                                                    color: "#fff",
                                                    minWidth: "120px"
                                                },
                                                width: "100%"
                                            }}
                                            InputProps={{
                                                style: { color: "#fff" }
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: "8px" }}>{round.ticketType}</td>
                                    <td style={{ padding: "8px" }}>{round.maxInstantPurchase}</td>
                                    <td style={{ padding: "8px" }}>{round.sellStartDateString}</td>
                                    <td style={{ padding: "8px" }}>{round.sellEndDate}</td>
                                    <td style={{ padding: "8px" }}>
                                        <TextField
                                            type="number"
                                            variant="standard"
                                            value={currentPrice}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                
                                                setRoundsPendingEdits(prev => ({
                                                    ...prev,
                                                    [round.roundId]: {
                                                        ...prev[round.roundId],
                                                        price: newValue,
                                                    },
                                                }));
                                            }}
                                            sx={{ 
                                                input: { 
                                                    color: "#fff",
                                                    minWidth: "80px"
                                                },
                                                width: "100%"
                                            }}
                                            InputProps={{
                                                style: { color: "#fff" },
                                                inputProps: { min: 0, step: "0.01" }
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: "8px" }}>{round.fee}</td>
                                    <td style={{ padding: "8px" }}>{round.totalParticipants}</td>
                                    <td style={{ padding: "8px" }}>{round.approvedParticipants}</td>
                                    <td style={{ padding: "8px" }}>{round.totalPaid} NIS</td>
                                    <td style={{ padding: "8px" }}>{round.isPrivate}</td>
                                    <td style={{ padding: "8px" }}>
                                        {hasPendingEdits && (
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <IconButton
                                                    color="success"
                                                    size="small"
                                                    onClick={async () => {
                                                        const changes = roundsPendingEdits[round.roundId];
                                                        console.log("Updating round:", round.roundId, "with changes:", changes);
                                                        
                                                        try {
                                                            const res = await fetch(`${process.env.REACT_APP_API_URL}/events/rounds/${round.roundId}`, {
                                                                method: "PATCH",
                                                                headers: { "Content-Type": "application/json" },
                                                                body: JSON.stringify(changes),
                                                            });
                                                            
                                                            if (res.ok) {
                                                                // Update the rounds state with new values
                                                                setRounds(prevRounds => {
                                                                    return prevRounds.map(r => {
                                                                        if (r.roundId === round.roundId) {
                                                                            return {
                                                                                ...r,
                                                                                roundName: changes.name !== undefined ? changes.name : r.roundName,
                                                                                originalRoundName: changes.name !== undefined ? changes.name : r.originalRoundName,
                                                                                price_NIS: changes.price !== undefined ? changes.price : r.price_NIS,
                                                                                originalPrice: changes.price !== undefined ? changes.price : r.originalPrice
                                                                            };
                                                                        }
                                                                        return r;
                                                                    });
                                                                });
                                                                
                                                                // Clear pending edits for this round
                                                                setRoundsPendingEdits(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated[round.roundId];
                                                                    return updated;
                                                                });
                                                                
                                                                console.log("Successfully updated round");
                                                            } else {
                                                                throw new Error(`HTTP ${res.status}: Failed to update round`);
                                                            }
                                                        } catch (error) {
                                                            console.error("Error updating round:", error);
                                                            alert(`Failed to update round: ${error.message}`);
                                                        }
                                                    }}
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={() => {
                                                        // Clear pending edits to revert to original values
                                                        setRoundsPendingEdits(prev => {
                                                            const updated = { ...prev };
                                                            delete updated[round.roundId];
                                                            return updated;
                                                        });
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Box>
        </Box>
    );

    // ✅ Fixed producers table rendering
    const renderProducersTable = () => (
        <TableContainer component={Paper} sx={{ width: "40%", bgcolor: "#1e1e1e", padding: 2 }}>
            <Typography variant="h6">Producers</Typography>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {producers.length > 0 ? (
                        producers.map((producer, index) => {
                            const editedProducer = editedProducers[index];
                            const hasPendingEdits = producersPendingEdits[producer.producerId];
                            const formattedPhone = producer.phoneNumber ? producer.phoneNumber.replace(/^(\+972|972)/, "0") : "N/A";
                            const whatsappUrl = producer.phoneNumber
                                ? `https://wa.me/${producer.phoneNumber.replace(/\D/g, "")}`
                                : null;

                            return (
                                <TableRow key={`${producer.producerId}-${index}`}>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <TextField
                                                variant="standard"
                                                size="small"
                                                placeholder="First Name"
                                                value={editedProducer?.firstName || producer.firstName || ""}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    
                                                    // Update editedProducers state
                                                    setEditedProducers(prev => {
                                                        const updated = [...prev];
                                                        if (updated[index]) {
                                                            updated[index] = { ...updated[index], firstName: newValue };
                                                        }
                                                        return updated;
                                                    });
                                                    
                                                    // Track pending edits
                                                    setProducersPendingEdits(prev => ({
                                                        ...prev,
                                                        [producer.producerId]: {
                                                            ...prev[producer.producerId],
                                                            firstName: newValue,
                                                        },
                                                    }));
                                                }}
                                                sx={{ input: { color: "#fff" } }}
                                            />
                                            <TextField
                                                variant="standard"
                                                size="small"
                                                placeholder="Last Name"
                                                value={editedProducer?.lastName || producer.lastName || ""}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    
                                                    // Update editedProducers state
                                                    setEditedProducers(prev => {
                                                        const updated = [...prev];
                                                        if (updated[index]) {
                                                            updated[index] = { ...updated[index], lastName: newValue };
                                                        }
                                                        return updated;
                                                    });
                                                    
                                                    // Track pending edits
                                                    setProducersPendingEdits(prev => ({
                                                        ...prev,
                                                        [producer.producerId]: {
                                                            ...prev[producer.producerId],
                                                            lastName: newValue,
                                                        },
                                                    }));
                                                }}
                                                sx={{ input: { color: "#fff" } }}
                                            />
                                            
                                            {hasPendingEdits && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={async () => {
                                                            const changes = producersPendingEdits[producer.producerId];
                                                            console.log("🔄 Updating producer:", producer.producerId, "with changes:", changes);
                                                            
                                                            try {
                                                                const res = await fetch(`${process.env.REACT_APP_API_URL}/events/producers/${producer.producerId}`, {
                                                                    method: "PATCH",
                                                                    headers: { "Content-Type": "application/json" },
                                                                    body: JSON.stringify(changes),
                                                                });
                                                                
                                                                if (res.ok) {
                                                                    // Clear pending edits for this producer
                                                                    setProducersPendingEdits(prev => {
                                                                        const updated = { ...prev };
                                                                        delete updated[producer.producerId];
                                                                        return updated;
                                                                    });
                                                                    
                                                                    // Update the main producers state to reflect changes
                                                                    setProducers(prev => {
                                                                        const updated = [...prev];
                                                                        updated[index] = {
                                                                            ...updated[index],
                                                                            firstName: changes.firstName || updated[index].firstName,
                                                                            lastName: changes.lastName || updated[index].lastName
                                                                        };
                                                                        return updated;
                                                                    });
                                                                    
                                                                    console.log("✅ Successfully updated producer");
                                                                } else {
                                                                    throw new Error("Failed to update producer");
                                                                }
                                                            } catch (error) {
                                                                console.error("❌ Error updating producer:", error);
                                                                // Optionally show user feedback
                                                            }
                                                        }}
                                                    >
                                                        <CheckIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => {
                                                            // Reset to original values
                                                            setEditedProducers(prev => {
                                                                const updated = [...prev];
                                                                updated[index] = {
                                                                    producerId: producer.producerId,
                                                                    firstName: producer.firstName || "",
                                                                    lastName: producer.lastName || "",
                                                                };
                                                                return updated;
                                                            });
                                                            
                                                            // Clear pending edits
                                                            setProducersPendingEdits(prev => {
                                                                const updated = { ...prev };
                                                                delete updated[producer.producerId];
                                                                return updated;
                                                            });
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>

                                    <TableCell>{producer.email}</TableCell>
                                    <TableCell>{formattedPhone}</TableCell>
                                    <TableCell>
                                        {whatsappUrl ? (
                                            <IconButton
                                                component="a"
                                                href={whatsappUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: "#25D366" }}
                                            >
                                                <WhatsAppIcon />
                                            </IconButton>
                                        ) : "N/A"}
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={4} align="center">No producers found.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box
            sx={{
                maxWidth: "90vw",
                mx: "auto",
                textAlign: "center",
                minHeight: "100vh",
                bgcolor: "#121212",
                color: "#fff",
                paddingBottom: "50px",
            }}
        >
            {loading && <CircularProgress sx={{ marginTop: 2 }} />}
            {error && <Typography color="error" sx={{ marginTop: 2 }}>{error}</Typography>}

            {/* Event Details Card */}
            {eventDetails && (
                <Card
                    sx={{
                        marginTop: 3,
                        bgcolor: "#1e1e1e",
                        color: "#fff",
                        textAlign: "left",
                        padding: 3,
                        width: "100%",
                    }}
                >

                    <CardContent>
                        {/* Event Title as Centered Header */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                            <TextField
                                variant="standard"
                                value={editedEventName}
                                onChange={(e) => {
                                    setEditedEventName(e.target.value);
                                    setShowEventNameActions(true);
                                }}
                                sx={{ input: { color: "#fff", fontWeight: "bold", fontSize: "1.5rem", textAlign: "center" } }}
                            />
                            {showEventNameActions && (
                                <>
                                    <IconButton
                                        size="small"
                                        color="success"
                                        onClick={async () => {
                                            try {
                                                const res = await fetch(`${process.env.REACT_APP_API_URL}/events/${eventDetails.shareIdentifier}`, {
                                                    method: "PATCH",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify({ title: editedEventName }),
                                                });
                                                if (!res.ok) throw new Error("Failed to update event name");
                                                setEventDetails({ ...eventDetails, title: editedEventName });
                                                setShowEventNameActions(false);
                                            } catch (err) {
                                                console.error(err);
                                            }
                                        }}
                                    >
                                        <CheckIcon fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            setEditedEventName(eventDetails.title);
                                            setShowEventNameActions(false);
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                            <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => navigate(`/eventDetails/${eventDetails.shareIdentifier}?tab=participants`)}
                                sx={{ ml: 2 }}
                            >
                                Participants
                            </Button>

                            {/* Refund All Participants Button */}
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => setOpenRefundDialog(true)}
                                disabled={refundLoading}
                                sx={{ 
                                    ml: 1,
                                    borderColor: "#f44336",
                                    color: "#f44336",
                                    "&:hover": {
                                        borderColor: "#d32f2f",
                                        backgroundColor: "rgba(244, 67, 54, 0.1)"
                                    }
                                }}
                            >
                                {refundLoading ? "Processing..." : "Refund All Participants"}
                            </Button>

                        </Box>


                        {/* ✅ Net Income Display */}
                        <Box
                            sx={{
                                bgcolor: "#222",
                                padding: 2,
                                borderRadius: "5px",
                                textAlign: "center",
                                fontSize: "2rem", // Very large font
                                fontWeight: "bold",
                                color: "#90caf9", // Highlight color
                                marginBottom: 3, // Space below title
                                width: "100%",
                            }}
                        >
                            EVENT NET INCOME: {(eventDetails.totalrevenue * eventDetails.fee).toFixed(2)} NIS
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                            {/* Left: Rendered Preview */}
                            {editedDescription.startsWith("[HTMLDESC]") ? (
                                <Box
                                    sx={{
                                        flex: 1,
                                        bgcolor: "#222",
                                        color: "#fff",
                                        padding: 2,
                                        borderRadius: "5px",
                                        maxHeight: "15em",
                                        overflow: "auto",
                                        border: "1px solid #444",
                                        direction: "rtl",
                                        textAlign: "right",
                                        "& *": { color: "#fff" }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: editedDescription.replace("[HTMLDESC]", "").trim() }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        flex: 1,
                                        bgcolor: "#222",
                                        color: "#fff",
                                        padding: 2,
                                        borderRadius: "5px",
                                        maxHeight: "15em",
                                        overflow: "auto",
                                        border: "1px solid #444",
                                        direction: "rtl",
                                        textAlign: "right",
                                        whiteSpace: "pre-wrap"
                                    }}
                                >
                                    {editedDescription}
                                </Box>
                            )}

                            {/* Right: Editable TextArea */}
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <TextField
                                    multiline
                                    fullWidth
                                    minRows={5}
                                    maxRows={10}
                                    value={editedDescription}
                                    onChange={(e) => {
                                        setEditedDescription(e.target.value);
                                        setShowDescriptionActions(true);
                                    }}
                                    sx={{
                                        bgcolor: "#222",
                                        borderRadius: "5px",
                                        "& textarea": {
                                            resize: "none",
                                            color: "#fff",
                                            direction: "rtl",
                                            textAlign: "right",
                                            lineHeight: "1.5",
                                        },
                                    }}
                                    InputLabelProps={{ style: { color: "#bbb" } }}
                                />

                                {showDescriptionActions && (
                                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                        <IconButton
                                            size="small"
                                            color="success"
                                            onClick={async () => {
                                                try {
                                                    const res = await fetch(
                                                        `${process.env.REACT_APP_API_URL}/events/${eventDetails.shareIdentifier}`,
                                                        {
                                                            method: "PATCH",
                                                            headers: { "Content-Type": "application/json" },
                                                            body: JSON.stringify({ description: editedDescription }),
                                                        }
                                                    );
                                                    if (!res.ok) throw new Error("Failed to update description");
                                                    setEventDetails({ ...eventDetails, description: editedDescription });
                                                    setShowDescriptionActions(false);
                                                } catch (err) {
                                                    console.error(err);
                                                }
                                            }}
                                        >
                                            <CheckIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                setEditedDescription(eventDetails.description);
                                                setShowDescriptionActions(false);
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </Box>


                        {/* Grid for Other Event Details */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, 1fr)",
                                gap: 2,
                            }}
                        >
                            <Typography><strong>Event ID:</strong> {eventDetails.id}</Typography>
                            <Typography><strong>Start Date:</strong> {new Date(eventDetails.eventStartDate).toLocaleDateString()}</Typography>
                            <Typography><strong>Created:</strong> {new Date(eventDetails.createdAt).toLocaleDateString()}</Typography>
                            <Typography><strong>Status:</strong> {eventDetails.status}</Typography>
                            <Typography><strong>Approval Status:</strong> {eventDetails.eventApprovalStatus}</Typography>
                            <Typography><strong>View Count:</strong> {eventDetails.viewCount}</Typography>
                            <Typography><strong>Participants:</strong> {eventDetails.totalparticipants}</Typography>
                            <Typography><strong>Approved Participants:</strong> {eventDetails.approvedparticipants}</Typography>
                            <Typography><strong>Tickets Sold:</strong> {eventDetails.ticketssold}</Typography>
                            <Typography><strong>Total Revenue:</strong> {Number(eventDetails.totalrevenue).toFixed(2)} NIS</Typography>
                        </Box>
                    </CardContent>

                </Card>
            )}

            {/* Event Rounds Table */}
            {rounds.length > 0 && renderRoundsTable()}

            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 4, marginTop: 3 }}>
                {/* ✅ Producers Table */}
                {renderProducersTable()}

                <TableContainer component={Paper} sx={{ width: "56%", bgcolor: "#1e1e1e", padding: 2 }}>
                    <Typography variant="h6">Supplier Payments</Typography>

                    {/* Add Row Button */}
                    {Array.isArray(supplierPayments) && (supplierPayments.length === 0 ||
                        Object.values(supplierPayments[supplierPayments.length - 1] || {}).some(value => value?.toString().trim() !== "")
                    ) ? (
                        <IconButton onClick={handleOpenAddPaymentDialog} sx={{ color: "#90caf9" }}>
                            <AddCircleIcon />
                        </IconButton>
                    ) : null}

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Supplier Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Payment</TableCell>
                                <TableCell>Payment Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {supplierPayments.map((row, index) => {
                                const supplier = suppliers.find(s => s.id === row.supplierId);
                                console.log("📝 suppliers:", JSON.stringify(suppliers, null, 2));
                                console.log("📝 supplier:", JSON.stringify(supplier, null, 2));
                                const supplierType = row.supplierType || supplier?.type || "";
                                const supplierPhone = row.supplierPhone || supplier?.phone || "";
                                const supplierId = row.supplierId || supplier?.id || "";
                                console.log("📝 supplierData " + supplierType + ", " + supplierPhone + ", " + supplierId);


                                return (
                                    <TableRow key={row.id || index}>
                                        {/* Supplier Name Dropdown */}
                                        <TableCell>
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={supplierId}
                                                onChange={(e) => {
                                                    const selectedSupplier = suppliers.find(s => s.id === e.target.value);
                                                    handleEditCell(index, "supplierId", selectedSupplier.id);
                                                    handleEditCell(index, "supplierType", selectedSupplier.type);
                                                    handleEditCell(index, "supplierPhone", selectedSupplier.phone);
                                                }}
                                            >
                                                {suppliers.map((s) => (
                                                    <MenuItem key={s.id} value={s.id}>
                                                        {s.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>

                                        {/* Supplier Type (Read-only) */}
                                        <TableCell>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                value={supplierType}
                                                InputProps={{ readOnly: true }}
                                            />
                                        </TableCell>

                                        {/* Supplier Phone (Read-only) */}
                                        <TableCell>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                value={supplierPhone}
                                                InputProps={{ readOnly: true }}
                                            />
                                        </TableCell>

                                        {/* Payment Input */}
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                variant="outlined"
                                                size="small"
                                                value={row.payment}
                                                onChange={(e) => handleEditCell(index, "payment", e.target.value)}
                                                onBlur={() => handleCellBlur(index, "payment")}
                                            />
                                        </TableCell>

                                        {/* Payment Date */}
                                        <TableCell>
                                            <TextField
                                                type="date"
                                                variant="outlined"
                                                size="small"
                                                value={row.paymentDate}
                                                onChange={(e) => handleEditCell(index, "paymentDate", e.target.value)}
                                                onBlur={() => handleCellBlur(index, "paymentDate")}
                                            />
                                        </TableCell>

                                        {/* Delete */}
                                        <TableCell>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <IconButton
                                                    component="a"
                                                    href={`https://wa.me/${supplierPhone.replace(/\D/g, "")}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ color: "#25D366" }}
                                                >
                                                    <WhatsAppIcon />
                                                </IconButton>

                                                <IconButton onClick={() => handleDeleteRow(row.id, index)} color="error">
                                                    ❌
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Dialog
                open={openPaymentDialog}
                onClose={handleCancelAddPayment}
                PaperProps={{
                    sx: {
                        backgroundColor: "#1e1e1e",
                        padding: 2,
                        width: "600px",
                    }
                }}
            >
                <DialogContent sx={{ overflow: "hidden" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Add Supplier Payment
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Supplier</Typography>
                            <Select
                                fullWidth
                                size="small"
                                value={newPaymentRow.supplier_id}
                                onChange={(e) => {
                                    const selected = suppliers.find(s => s.id === e.target.value);
                                    setNewPaymentRow({
                                        ...newPaymentRow,
                                        supplier_id: selected.id,
                                        supplierType: selected.type,
                                        supplierPhone: selected.phone,
                                    });
                                }}
                            >
                                {suppliers.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Type</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={newPaymentRow.supplierType}
                                InputProps={{ readOnly: true }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Phone</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={newPaymentRow.supplierPhone}
                                InputProps={{ readOnly: true }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Payment</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                value={newPaymentRow.payment}
                                onChange={(e) => setNewPaymentRow({ ...newPaymentRow, payment: e.target.value })}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Payment Date</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                value={newPaymentRow.paymentDate}
                                onChange={(e) => setNewPaymentRow({ ...newPaymentRow, paymentDate: e.target.value })}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <IconButton onClick={handleConfirmAddPayment} color="success">
                        <CheckIcon />
                    </IconButton>
                    <IconButton onClick={handleCancelAddPayment} color="error">
                        <CloseIcon />
                    </IconButton>
                </DialogActions>
            </Dialog>
            {/* Event Balance Table */}
            {/* Event Balance Table */}
            <Box sx={{ marginTop: 5, display: "flex", justifyContent: "center" }}>
                <TableContainer component={Paper} sx={{ width: "50%", bgcolor: "#1e1e1e" }}>
                    <Typography variant="h6" sx={{ margin: 2, textAlign: "center" }}>
                        Event Balance
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "#333" }}>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>Item</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Tickets Revenue Row */}
                            <TableRow>
                                <TableCell>Tickets</TableCell>
                                <TableCell>{Number(eventDetails?.totalrevenue || 0).toFixed(2)} NIS</TableCell>
                            </TableRow>

                            {/* Supplier Payments (Negative) */}
                            {supplierPayments.map((payment, idx) => {
                                const supplier = suppliers.find(s => s.id === payment.supplierId);
                                const name = supplier?.name || payment.supplierName || "Unknown Supplier";
                                return (
                                    <TableRow key={idx}>
                                        <TableCell>Supplier - {name}</TableCell>
                                        <TableCell>-{Number(payment.payment || 0).toFixed(2)} NIS</TableCell>
                                    </TableRow>
                                );
                            })}

                            {/* Final Total Row */}
                            <TableRow sx={{ bgcolor: "#222" }}>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>Total Producer Income</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                                    {(() => {
                                        const totalRevenue = Number(eventDetails?.totalrevenue || 0);
                                        const totalPayments = supplierPayments.reduce((acc, cur) => acc + Number(cur.payment || 0), 0);
                                        return (totalRevenue - totalPayments).toFixed(2);
                                    })()} NIS
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Refund Confirmation Dialog */}
            <Dialog
                open={openRefundDialog}
                onClose={() => setOpenRefundDialog(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: "#1e1e1e",
                        color: "#fff",
                        padding: 2,
                        minWidth: "400px",
                    }
                }}
            >
                <DialogContent>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography variant="h6" sx={{ color: "#f44336", mb: 2 }}>
                            ⚠️ Refund All Participants
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Are you sure you want to refund all participants for this event?
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#ff9800", fontWeight: "bold" }}>
                            This action is NOT reversible!
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: "#bbb" }}>
                            All paid participants will be rejected and refunded automatically.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 2 }}>
                    <Button
                        onClick={() => setOpenRefundDialog(false)}
                        variant="outlined"
                        sx={{ 
                            borderColor: "#666", 
                            color: "#fff",
                            "&:hover": { borderColor: "#888" }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRefundAllParticipants}
                        variant="contained"
                        color="error"
                        sx={{
                            backgroundColor: "#f44336",
                            "&:hover": { backgroundColor: "#d32f2f" }
                        }}
                    >
                        Yes, Refund All
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Refund Result Dialog */}
            <Dialog
                open={openRefundResultDialog}
                onClose={() => setOpenRefundResultDialog(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: "#1e1e1e",
                        color: "#fff",
                        padding: 2,
                        minWidth: "500px",
                        maxWidth: "600px",
                    }
                }}
            >
                <DialogContent>
                    <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Refund Process Complete
                        </Typography>
                        
                        {refundResult && (
                            <>
                                <Typography variant="body1" sx={{ mb: 3, whiteSpace: "pre-wrap" }}>
                                    {refundResult.message}
                                </Typography>
                                
                                {refundResult.details && (
                                    <Box sx={{ 
                                        bgcolor: "#333", 
                                        p: 2, 
                                        borderRadius: 1, 
                                        mb: 2,
                                        textAlign: "left"
                                    }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                                            Summary:
                                        </Typography>
                                        <Typography variant="body2">
                                            • Total Participants: {refundResult.details.totalParticipants}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "#4caf50" }}>
                                            • Successful Refunds: {refundResult.details.successfulRejections}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "#f44336" }}>
                                            • Failed Refunds: {refundResult.details.failedRejections}
                                        </Typography>
                                        
                                        {refundResult.details.failures && refundResult.details.failures.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" sx={{ color: "#f44336", mb: 1 }}>
                                                    Failed Refunds:
                                                </Typography>
                                                {refundResult.details.failures.map((failure, index) => (
                                                    <Typography key={index} variant="body2" sx={{ 
                                                        fontSize: "0.8rem", 
                                                        color: "#ff9800",
                                                        ml: 1
                                                    }}>
                                                        • {failure.guestName} (Ticket #{failure.ticketId}): {failure.error}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <Button
                        onClick={() => setOpenRefundResultDialog(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: "#1976d2",
                            "&:hover": { backgroundColor: "#1565c0" }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default EventDetails;
